import React, { FunctionComponent } from "react";
import { Icon, Label, Stack, Text } from "sprint-common-web";

import {
    FileRecordRejectionDetails,
    FileRecordStatusDetails,
    FileRecordWaitingForClientResponseDetails,
    isFileRecordRejectionDetails
} from "../../types/FileRecordStatusDetails";

export type FileRecordStatusDetailsTextProps = {
    statusDetails?: FileRecordStatusDetails;
};

export const LabeledFileRecordStatusDetailsText: FunctionComponent<FileRecordStatusDetailsTextProps> = ({
    statusDetails
}: FileRecordStatusDetailsTextProps) => {
    return (
        <Stack data-testId="status-details-field" styles={{ root: { marginBottom: 10 } }}>
            <Label data-testId="status-details-field-label">Status details</Label>
            {renderFileRecordStatusDetailsText({ statusDetails })}
        </Stack>
    );
};

const MAX_LENGTH = 40;
const FONT_SIZE = 14;

export const renderFileRecordStatusDetailsText = ({
    statusDetails,
    maxLength = MAX_LENGTH,
    fontSize = FONT_SIZE
}: {
    statusDetails?: FileRecordStatusDetails;
    maxLength?: number;
    fontSize?: number;
}): React.ReactElement | null => {
    if (!statusDetails) {
        return (
            <Text data-testId="status-details-field-text" maxLength={MAX_LENGTH}>
                -
            </Text>
        );
    }

    let segments: string[];

    if (isFileRecordRejectionDetails(statusDetails)) {
        segments = getRejectionDetailsSegments(statusDetails);
    } else {
        segments = getRenderWaitingForClientResponseDetailsSegments(statusDetails);
    }

    const tooltipView = (
        <Stack data-testid="status-details-field-tooltip-text">
            {segments[0]}
            <br />
            {segments[1]}
        </Stack>
    );

    return (
        <Stack styles={{ root: { display: "flex", flexFlow: "row" } }}>
            <Text
                data-testid="status-details-field-text"
                testId="status-details-field-tooltip"
                maxLength={maxLength}
                tooltip={tooltipView}
                styles={{ root: { fontSize, cursor: "pointer" } }}
            >
                {segments[0]}
                {statusDetails && <Icon iconName="Info" size={11} padding="0 0 0 2px" />}
            </Text>
        </Stack>
    );
};

const getRejectionDetailsSegments = (statusDetails: FileRecordRejectionDetails): string[] => {
    const segment1 = [statusDetails.reason, statusDetails.details].filter(Boolean).join(": ");
    const segment2 = [statusDetails.courseOfAction, statusDetails.fixedBilling].filter(Boolean).join(": ");

    return [segment1, segment2].filter(Boolean);
};

const getRenderWaitingForClientResponseDetailsSegments = (
    statusDetails: FileRecordWaitingForClientResponseDetails
): string[] => {
    const segment1 = [statusDetails.details].filter(Boolean).join(": ");
    const segment2 = [statusDetails.courseOfAction, statusDetails.fixedBilling].filter(Boolean).join(": ");

    return [segment1, segment2].filter(Boolean);
};
